import { useCallback } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import type { ZeroMutateClient } from "./useZeroMutationUtils";

export type StreamTakerContact = {
  id: string;
  streamTakerID: string;
  personID: string;
  description: string;
  person?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
  } | null;
};

export type StreamTakerAudioPairTest = {
  id: string;
  streamTakerID: string;
  signalAudioFeedID: string;
  tested: boolean;
  preTestDone: boolean;
  onDayTestDone: boolean;
};

export type StreamTaker = {
  id: string;
  projectID: string;
  name: string;
  deliveryMethod: string;
  url: string;
  signalID: string | null;
  contact: string;
  notes: string;
  testDate: number | null;
  tested: boolean;
  preTestDone: boolean;
  onDayTestDone: boolean;
  signal?: {
    id: string;
    name: string;
    videoFeed?: { id: string; name: string } | null;
    audioFeedLinks?: Array<{
      id: string;
      sortOrder: number;
      audioFeed?: { id: string; name: string } | null;
    }>;
  } | null;
  contacts?: StreamTakerContact[];
  audioPairTests?: StreamTakerAudioPairTest[];
};

async function checkServer(
  server: Promise<{ type: string; error?: { message: string } }>,
  errorPrefix: string,
) {
  const result = await server.catch(() => ({
    type: "error" as const,
    error: { message: "Connection lost" },
  }));
  if (result.type === "error") {
    toast.error(`${errorPrefix}: ${result.error?.message ?? "Unknown error"}`);
    return false;
  }
  return true;
}

export const useStreamTakers = (projectId: string | undefined) => {
  const query = projectId
    ? appQueries.streamTakers.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const takers = (data ?? []) as StreamTaker[];
  return {
    data: takers,
    isLoading:
      !!projectId && details.type !== "complete" && takers.length === 0,
  };
};

export const useStreamTakerMutations = () => {
  const zero = useZero() as ZeroMutateClient;

  const createTaker = useCallback(
    async (
      input: {
        projectID: string;
        name: string;
        deliveryMethod?: string;
        url?: string;
        signalID?: string | null;
        notes?: string;
        testDate?: number | null;
      },
      options?: { onSuccess?: () => void },
    ) => {
      const { server } = zero.mutate(
        appMutators.streamTakers.create({
          id: generateUUID(),
          projectID: input.projectID,
          name: input.name,
          deliveryMethod: input.deliveryMethod ?? "",
          url: input.url ?? "",
          signalID: input.signalID ?? null,
          contact: "",
          notes: input.notes ?? "",
          testDate: input.testDate ?? null,
          tested: false,
          preTestDone: false,
          onDayTestDone: false,
        }),
      );
      if (await checkServer(server, "Failed to add stream taker")) {
        toast.success("Stream taker added");
        options?.onSuccess?.();
      }
    },
    [zero],
  );

  const updateTaker = useCallback(
    (input: {
      id: string;
      projectID: string;
      name?: string;
      deliveryMethod?: string;
      url?: string;
      signalID?: string | null;
      notes?: string;
      testDate?: number | null;
      preTestDone?: boolean;
      onDayTestDone?: boolean;
    }) => {
      const { server } = zero.mutate(appMutators.streamTakers.update(input));
      void checkServer(server, "Failed to update stream taker");
    },
    [zero],
  );

  const deleteTaker = useCallback(
    async (
      input: { id: string; projectId: string },
      options?: { onSuccess?: () => void },
    ) => {
      const { server } = zero.mutate(
        appMutators.streamTakers.delete({
          id: input.id,
          projectID: input.projectId,
        }),
      );
      if (await checkServer(server, "Failed to remove stream taker")) {
        toast.success("Stream taker removed");
        options?.onSuccess?.();
      }
    },
    [zero],
  );

  const createContact = useCallback(
    async (
      input: {
        streamTakerID: string;
        personID: string;
        description?: string;
      },
      options?: { onSuccess?: () => void },
    ) => {
      const { server } = zero.mutate(
        appMutators.streamTakerContacts.create({
          id: generateUUID(),
          streamTakerID: input.streamTakerID,
          personID: input.personID,
          description: input.description ?? "",
        }),
      );
      if (await checkServer(server, "Failed to add contact")) {
        toast.success("Contact added");
        options?.onSuccess?.();
      }
    },
    [zero],
  );

  const updateContact = useCallback(
    (input: {
      id: string;
      streamTakerID: string;
      personID?: string;
      description?: string;
    }) => {
      const { server } = zero.mutate(
        appMutators.streamTakerContacts.update(input),
      );
      void checkServer(server, "Failed to update contact");
    },
    [zero],
  );

  const deleteContact = useCallback(
    (input: { id: string; streamTakerID: string }) => {
      const { server } = zero.mutate(
        appMutators.streamTakerContacts.delete(input),
      );
      void checkServer(server, "Failed to remove contact").then((ok) => {
        if (ok) toast.success("Contact removed");
      });
    },
    [zero],
  );

  const toggleAudioPairTest = useCallback(
    (
      taker: StreamTaker,
      signalAudioFeedID: string,
      field: "preTestDone" | "onDayTestDone",
    ) => {
      const existing = taker.audioPairTests?.find(
        (t) => t.signalAudioFeedID === signalAudioFeedID,
      );
      if (existing) {
        const { server } = zero.mutate(
          appMutators.streamTakerAudioPairTests.update({
            id: existing.id,
            streamTakerID: taker.id,
            [field]: !existing[field],
          }),
        );
        void checkServer(server, "Failed to update test");
      } else {
        const { server } = zero.mutate(
          appMutators.streamTakerAudioPairTests.create({
            id: generateUUID(),
            streamTakerID: taker.id,
            signalAudioFeedID,
            tested: false,
            preTestDone: false,
            onDayTestDone: false,
            [field]: true,
          }),
        );
        void checkServer(server, "Failed to update test");
      }
    },
    [zero],
  );

  return {
    createTaker,
    updateTaker,
    deleteTaker,
    createContact,
    updateContact,
    deleteContact,
    toggleAudioPairTest,
  };
};
